import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    Button, Box, Typography, TextField, Autocomplete, 
    ToggleButton, ToggleButtonGroup, Stack, Divider, 
    IconButton, Paper
} from '@mui/material';
import axios from 'axios';
import { GiTennisCourt } from "react-icons/gi";
import { useAuth } from '../provider/auth';
import { FaCalendarAlt, FaClock, FaUsers, FaUser } from "react-icons/fa";
import { FaHourglassHalf } from "react-icons/fa6";
import { MdClose, MdDelete, MdBlock, MdCheckCircle } from "react-icons/md";
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../App.js';
import ConfirmModal from './ConfirmModal';

function BookingModal({ show, onHide, free, slot, court, data, players, events, profile }) {
    const [confirmConfig, setConfirmConfig] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {},
        type: 'question'
    });
    const { user } = useAuth();

    const [gameMode, setGameMode] = useState("single");
    const [duration, setDuration] = useState(1);
    const [teammates, setTeammates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [members, setMembers] = useState([]);
    const [player, setPlayer] = useState(null);
    const notStartedYet = dayjs(data?.startTime || slot).isAfter(dayjs());

    useEffect(() => {
        if (profile && profile.player) {
            const familyMembersData = (profile.familyMembers || []).map(member => ({
                id: member._id,
                name: `${member.firstName} ${member.lastName}`,
            }));
            const playerData = {
                id: profile.player._id,
                name: `${profile.player.firstName} ${profile.player.lastName}`,
            };
            setMembers([...familyMembersData, playerData]);
            setPlayer(playerData);
        }
    }, [profile]);

    useEffect(() => {
        const numberOfTeammates = gameMode === "single" ? 1 : 3;
        if (gameMode === "single") {
            setDuration(1);
        }
        setTeammates(prev => {
            const firstPartner = prev[0] || null;
            const newTeammates = Array.from({ length: numberOfTeammates }, () => null);
            if (firstPartner) {
                newTeammates[0] = firstPartner;
            }
            return newTeammates;
        });
    }, [gameMode]);

    function getNameById(id, fallback) {
        if (!id) return fallback || "Unbekannt";
        if (typeof id === 'object') return (id.name || `${id.firstName} ${id.lastName}`).trim() || fallback || "Unbekannt";
        const found = players?.find(p => String(p.id) === String(id));
        return found ? found.name.trim() : (fallback || "Unbekannt");
    }

    function getEventById(id) {
        if (!id) return "Unbekannt";
        return events?.find(item => item._id === id)?.name || "Unbekannt";
    }

    const handleDelete = (id) => {
        setConfirmConfig({
            open: true,
            title: 'Buchung löschen',
            message: 'Möchtest du diese Buchung wirklich löschen?',
            onConfirm: () => executeDelete(id),
            type: 'delete'
        });
    };

    const executeDelete = async (id) => {
        setLoading(true);
        try {
            await axios.delete(`${API_BASE_URL}/api/bookings/${id}`);
            toast.success("Buchung erfolgreich gelöscht");
            onHide(true);
        } catch (error) {
            toast.error(error.response?.data?.message || "Fehler beim Löschen");
        } finally {
            setLoading(false);
        }
    };

    const handleBooking = async () => {
        const validTeammates = teammates.filter(t => t && t.id);
        if (validTeammates.length === 0) return toast.error("Wähle Mitspieler aus");
        if (gameMode === "double" && validTeammates.length !== 3) return toast.error("Wähle drei Mitspieler aus");
        if (!player) return toast.error("Bitte wähle einen Spieler aus.");

        const booking = {
            mainPlayer: user.playerId,
            playerId: player?.id || user.playerId,
            courtId: court,
            startTime: slot,
            endTime: dayjs(slot).add(duration, "hour").toISOString(),
            gameType: gameMode,
            partners: validTeammates.map(t => t.id)
        };

        setLoading(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/api/booking`, booking);
            toast.success(res.data.message || "Buchung erfolgreich");
            onHide(true);
        } catch (error) {
            toast.error(error.response?.data?.message || "Fehler bei der Buchung");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateBookingStatus = (bookingId, newStatus) => {
        const action = newStatus === 1 ? "stornieren" : "reaktivieren";
        setConfirmConfig({
            open: true,
            title: `Termin ${action}`,
            message: `Möchtest du diesen Termin wirklich ${action}?`,
            onConfirm: () => executeUpdateBookingStatus(bookingId, newStatus),
            type: 'warning'
        });
    };

    const executeUpdateBookingStatus = async (bookingId, newStatus) => {
        setLoading(true);
        try {
            await axios.put(`${API_BASE_URL}/api/bookings/${bookingId}`, { status: newStatus });
            toast.success(`Termin erfolgreich ${newStatus === 1 ? 'storniert' : 'reaktiviert'}`);
            onHide(true);
        } catch (error) {
            toast.error(error.response?.data?.message || "Fehler bei Statusänderung");
        } finally {
            setLoading(false);
        }
    };

    const getAvailablePlayers = () => {
        if (!players) return [];
        const selectedIds = teammates.filter(t => t?.id).map(t => String(t.id));
        const guestEmail = (process.env.REACT_APP_GUEST_EMAIL || 'gast@tc-opfingen.de').toLowerCase();
        
        return players.filter(p => {
            if (!p?.id || String(p.id) === String(player?.id)) return false;
            if (p.email?.toLowerCase() === guestEmail) return true;
            return !selectedIds.includes(String(p.id));
        }).sort((a, b) => {
            const aIsGuest = a.email?.toLowerCase() === guestEmail;
            const bIsGuest = b.email?.toLowerCase() === guestEmail;
            if (aIsGuest && !bIsGuest) return -1;
            if (!aIsGuest && bIsGuest) return 1;
            return (a.name || '').localeCompare(b.name || '');
        });
    };

    return (
        <Dialog open={show} onClose={() => onHide()} fullWidth maxWidth="xs">
            <DialogTitle sx={{ 
                bgcolor: 'primary.main', color: 'white', px: 3, py: 2,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <GiTennisCourt size={28} />
                    <Typography variant="h6" fontWeight="bold">Platz {court}</Typography>
                </Box>
                <IconButton onClick={() => onHide()} sx={{ color: 'white' }} size="small">
                    <MdClose />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pt: 1 }}>
                <Stack spacing={4} sx={{ mt: 4 }}>
                    {/* Header Info */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <FaCalendarAlt size={22} color="#666" />
                            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                                {dayjs(data?.startTime || slot).format("DD.MM.YYYY")}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <FaClock size={22} color="#666" />
                            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                                {dayjs(data?.startTime || slot).format("HH:mm")} Uhr
                            </Typography>
                        </Box>
                    </Box>

                    {free ? (
                        <>
                            {/* Mode Selection */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">SPIELMODUS</Typography>
                                <ToggleButtonGroup
                                    value={gameMode}
                                    exclusive
                                    onChange={(e, val) => val && setGameMode(val)}
                                    size="small"
                                    color="primary"
                                >
                                    <ToggleButton value="single" sx={{ px: 3 }}>Einzel</ToggleButton>
                                    <ToggleButton value="double" sx={{ px: 3 }}>Doppel</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {/* Duration Selection */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FaHourglassHalf size={14} color="#666" />
                                    <Typography variant="caption" color="text.secondary">DAUER</Typography>
                                </Box>
                                <ToggleButtonGroup
                                    value={duration}
                                    exclusive
                                    onChange={(e, val) => val && setDuration(val)}
                                    size="small"
                                    color="primary"
                                >
                                    <ToggleButton value={1} sx={{ px: 2 }}>1 Stunde</ToggleButton>
                                    {gameMode === "double" && <ToggleButton value={2} sx={{ px: 2 }}>2 Stunden</ToggleButton>}
                                </ToggleButtonGroup>
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                                    Ende: {dayjs(slot).add(duration, "hour").format("HH:mm")} Uhr
                                </Typography>
                            </Box>

                            <Divider />

                            {/* Player Selection */}
                            <Box>
                                {members.length > 0 ? (
                                    <Autocomplete
                                        size="small"
                                        options={members}
                                        getOptionLabel={(o) => (o.name || "").trim()}
                                        value={player}
                                        onChange={(e, val) => setPlayer(val)}
                                        renderInput={(p) => <TextField {...p} label="Spieler" />}
                                        sx={{ mb: 2 }}
                                        disableClearable={true}
                                    />
                                ) : (
                                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <FaUser color="#666" />
                                        <Typography variant="body2">
                                            {getNameById(profile?.player?._id)}
                                        </Typography>
                                    </Box>
                                )}

                                <Stack spacing={1.5}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <FaUsers /> MITSPIELER
                                    </Typography>
                                    {teammates.map((t, i) => (
                                        <Autocomplete
                                            key={i}
                                            size="small"
                                            options={getAvailablePlayers()}
                                            getOptionLabel={(o) => (o?.name || "").trim()}
                                            value={t}
                                            onChange={(e, val) => {
                                                const nt = [...teammates];
                                                nt[i] = val;
                                                setTeammates(nt);
                                            }}
                                            renderInput={(p) => <TextField {...p} label="Mitspieler" />}
                                        />
                                    ))}
                                </Stack>

                                {teammates.some(t => t?.email?.toLowerCase() === (process.env.REACT_APP_GUEST_EMAIL || 'gast@tc-opfingen.de').toLowerCase()) && (() => {
                                    const guestCount = teammates.filter(t => t?.email?.toLowerCase() === (process.env.REACT_APP_GUEST_EMAIL || 'gast@tc-opfingen.de').toLowerCase()).length;
                                    const rate = gameMode === 'single' ? 5 : 2.5;
                                    const totalPrice = (guestCount * duration * rate).toFixed(2).replace('.', ',');
                                    
                                    return (
                                        <Box sx={{ 
                                            mt: 2, p: 1.5, bgcolor: 'rgba(211, 47, 47, 0.05)', 
                                            borderLeft: '4px solid #d32f2f', borderRadius: 1 
                                        }}>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                justifyContent: 'space-between', 
                                                alignItems: { xs: 'flex-start', sm: 'center' }, 
                                                mb: 0.5,
                                                gap: { xs: 0.5, sm: 0 }
                                            }}>
                                                <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
                                                    HINWEIS GASTSPIELER
                                                </Typography>
                                                <Typography variant="subtitle2" color="error" sx={{ fontWeight: 'bold' }}>
                                                    Preis: {totalPrice} €
                                                </Typography>
                                            </Box>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
                                                Bei Nutzung der Tennisplätze mit einem Gastspieler (Nicht-Mitglied) fällt eine Platzgebühr in Höhe von 5,00 € (Einzel) bzw. 2,50 € (Doppel) pro Gast/Stunde an.
                                            </Typography>
                                        </Box>
                                    );
                                })()}
                            </Box>
                        </>
                    ) : (
                        data && (
                            <Box sx={{ textAlign: 'center', py: 1 }}>
                                {(() => {
                                    const eventObj = data.eventId ? events?.find(item => item._id === data.eventId) : null;
                                    return (
                                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', textTransform: 'uppercase' }}>
                                                {data.eventId ? (eventObj?.name || "Termin-Details") : "Buchungsdetails"}
                                            </Typography>
                                            
                                            <Stack spacing={1.5}>
                                                {!data.eventId && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Modus:</Typography>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {data.gameType === "single" ? "Einzel" : "Doppel"}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" color="text.secondary">Dauer:</Typography>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {dayjs(data.endTime).diff(dayjs(data.startTime), 'hour')} Std (bis {dayjs(data.endTime).format("HH:mm")})
                                                    </Typography>
                                                </Box>

                                                {data.eventId && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Plätze:</Typography>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {eventObj?.courts ? [...eventObj.courts].sort((a, b) => a - b).join(', ') : court}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {!data.eventId && (
                                                    <Box sx={{ mt: 1, textAlign: 'left' }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>SPIELER</Typography>
                                                        <Paper sx={{ p: 1.5, bgcolor: 'white' }}>
                                                            <Stack spacing={0.5}>
                                                                <Typography variant="body2">
                                                                    {getNameById(data.playerId, data.playerSnapshot)}
                                                                </Typography>
                                                                {data.partners?.map((p, idx) => (
                                                                    <Typography key={idx} variant="body2">
                                                                        {getNameById(p, data.partnersSnapshot?.[idx])}
                                                                    </Typography>
                                                                ))}
                                                            </Stack>
                                                        </Paper>
                                                    </Box>
                                                )}
                                            </Stack>
                                        </Paper>
                                    );
                                })()}
                            </Box>
                        )
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2.5, bgcolor: '#f8f9fa' }}>
                <Box sx={{ width: '100%', display: 'flex', gap: 1.5, justifyContent: 'center' }}>
                    {data && data.eventId && user.role >= 1 && notStartedYet && (
                        <Button 
                            variant="contained" 
                            color={data.status === 1 ? "success" : "warning"}
                            onClick={() => handleUpdateBookingStatus(data._id, data.status === 1 ? 0 : 1)}
                            disabled={loading}
                            startIcon={data.status === 1 ? <MdCheckCircle /> : <MdBlock />}
                            fullWidth
                        >
                            {data.status === 1 ? "Reaktivieren" : "Stornieren"}
                        </Button>
                    )}
                    
                    {data && !data.eventId && notStartedYet && (
                        (data.playerId === user.playerId || data.mainPlayer === user.playerId || 
                         data.partners?.includes(user.playerId) || user.role >= 1)
                    ) && (
                        <Button 
                            variant="outlined" 
                            color="error" 
                            onClick={() => handleDelete(data._id)}
                            disabled={loading}
                            startIcon={<MdDelete />}
                            fullWidth
                        >
                            Löschen
                        </Button>
                    )}

                    {free && notStartedYet && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleBooking}
                            disabled={loading}
                            fullWidth
                            sx={{ fontWeight: 'bold' }}
                        >
                            {loading ? "Wird gebucht..." : "Jetzt buchen"}
                        </Button>
                    )}
                </Box>
            </DialogActions>

            <ConfirmModal 
                open={confirmConfig.open}
                onClose={() => setConfirmConfig({ ...confirmConfig, open: false })}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
            />
        </Dialog>
    );
}

export default BookingModal;
